"use client";

import React, { useEffect, useRef } from "react";

interface MarqueeClientProps {
  children: React.ReactNode;
}

export function MarqueeClient({ children }: MarqueeClientProps) {
  const marqueeRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const copiesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let isCalculating = false;

    const setMarqueeContents = () => {
      if (isCalculating) return; // Prevent recursive calls
      isCalculating = true;

      if (!marqueeRef.current || !textRef.current || !copiesRef.current) {
        isCalculating = false;
        return;
      }

      // Wait for images to load before calculating
      const images = textRef.current.querySelectorAll("img");
      const imagePromises = Array.from(images).map((img) => {
        if (img.complete) return Promise.resolve();
        return new Promise((resolve) => {
          img.onload = resolve;
          img.onerror = resolve; // Still resolve on error to not block
        });
      });

      Promise.all(imagePromises)
        .then(() => {
          if (!textRef.current || !copiesRef.current || !marqueeRef.current) {
            isCalculating = false;
            return;
          }

          const windowWidth = window.innerWidth;
          const textWidth = textRef.current.offsetWidth;

          // Ensure we have valid dimensions
          if (textWidth <= 0 || windowWidth <= 0) {
            isCalculating = false;
            return;
          }

          const numberOfCopies = Math.ceil(windowWidth / textWidth) + 1;

          // Set CSS custom properties
          marqueeRef.current.style.setProperty(
            "--textWidth",
            textWidth.toString()
          );

          // Clear and populate copies
          copiesRef.current.innerHTML = "";
          for (let i = 0; i < numberOfCopies; i++) {
            const clone = textRef.current.cloneNode(true) as HTMLElement;
            copiesRef.current.appendChild(clone);
          }

          isCalculating = false;
        })
        .catch(() => {
          isCalculating = false;
        });
    };

    // Set initial content with a slight delay to ensure DOM is ready
    const timer = setTimeout(setMarqueeContents, 100);

    // Update on resize with debouncing
    let resizeTimer: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(setMarqueeContents, 150);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      clearTimeout(timer);
      clearTimeout(resizeTimer);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <div ref={marqueeRef} className="marquee">
      <div className="marquee__content">
        <div ref={textRef} className="marquee__text">
          {children}
        </div>
        <div
          ref={copiesRef}
          className="marquee__copies"
          aria-hidden="true"
        ></div>
      </div>
    </div>
  );
}
